import {
  MapControls,
  OrthographicCamera,
  PerspectiveCamera,
} from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useEffect, useState } from 'react';
import { debounceTime, fromEvent, tap } from 'rxjs';
import type { Vector3Tuple } from 'three';
import { MOUSE, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useToolSettingStore } from '@/store';
import { subVector } from '@/util';

import { useInitializeCamera, useSaveAndRestoreCamera } from './hook';

type ToolPcdCameraProps = {
  enabledControlsPan?: boolean;
  enabledControlsRotate?: boolean;
  enabledControlsUiEvent?: boolean;

  initCameraOptions?: {
    position?: Vector3Tuple;
    rotation?: Vector3Tuple;
    up?: Vector3Tuple;
    far?: number;
    near?: number;
    orthographic?: {
      zoom?: number;
    };
    perpective?: {
      zoom?: number;
    };
  };

  mode?: 'orthographic' | 'perspective';
};

const ToolPcdCamera = ({
  enabledControlsPan,
  enabledControlsRotate,
  enabledControlsUiEvent,

  initCameraOptions,

  mode,
}: ToolPcdCameraProps) => {
  const { camera, controls } = useThree();
  const { focusedBoxId, pcdIndexBoxListMap, getCurrentBoxList, focusBox } =
    useLabelingBoxContext();
  const [far, _setFar] = useState(initCameraOptions?.far ?? 3000);
  const [near, _setNear] = useState(initCameraOptions?.near ?? 0.001);
  const { orbitInfo } = useToolSettingStore();
  const { horizonReverse, verticalReverse } = orbitInfo;

  useInitializeCamera({ initCameraOptions });
  useSaveAndRestoreCamera();
  useSubscriptions(
    () => [
      fromEvent<Event>(window, 'resize')
        .pipe(
          debounceTime(0),
          tap(() => camera.updateProjectionMatrix()),
        )
        .subscribe(),

      fromEvent<Event>(window, 'fullscreenchange')
        .pipe(tap(() => window.dispatchEvent(new Event('resize'))))
        .subscribe(),
    ],
    [camera],
  );

  useEffect(() => {
    if (!focusedBoxId || !controls) return;

    const box = getCurrentBoxList().find(box => box.id === focusedBoxId);

    if (!box) return;

    const newTarget = new Vector3(...box.position);
    const delta = subVector(newTarget, controls.target);

    camera.position.add(delta);
    controls.target.copy(newTarget);

    controls.update();

    focusBox();
  }, [focusedBoxId, pcdIndexBoxListMap, getCurrentBoxList]);

  return (
    <>
      <OrthographicCamera
        far={far}
        near={near}
        zoom={initCameraOptions?.orthographic?.zoom}
        makeDefault={mode === 'orthographic'}
      />
      <PerspectiveCamera
        far={far}
        near={near}
        zoom={initCameraOptions?.perpective?.zoom}
        makeDefault={mode === 'perspective'}
      />

      <MapControls
        minDistance={5}
        maxDistance={1500}
        mouseButtons={{
          LEFT: MOUSE.PAN,
          MIDDLE: MOUSE.DOLLY,
          RIGHT: MOUSE.ROTATE,
        }}
        zoomSpeed={1.5}
        rotateSpeed={0.25}
        enabled={enabledControlsUiEvent}
        enableDamping={false}
        enablePan={enabledControlsPan}
        minPolarAngle={enabledControlsRotate ? -Math.PI : 0}
        maxPolarAngle={enabledControlsRotate ? Math.PI : 0}
        reverseHorizontalOrbit={horizonReverse}
        reverseVerticalOrbit={verticalReverse}
        makeDefault
      />
    </>
  );
};

export default ToolPcdCamera;
